import axios, { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { camelizeKeys, decamelize, decamelizeKeys } from 'humps';

import cookies from 'js-cookie';

const axiosInstance = axios.create();

// Convert a response's keys to camelCase.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (!response.headers) {
    return response;
  }

  // Typescript for some reason cannot infer that `response.headers` cannot be null or undefined at this point
  response.headers = new AxiosHeaders(response.headers ? { ...response.headers } : {});

  if (response.data && (response.headers as AxiosHeaders)['content-type'] === 'application/json') {
    response.data = camelizeKeys(response.data);
  }
  return response;
};

// Convert a request's keys to the appropriate format.
const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const newConfig: InternalAxiosRequestConfig = { ...config };

  // Set CSRF headers
  const csrftoken = cookies.get('csrftoken');
  if (config.headers && csrftoken) {
    newConfig.headers = new AxiosHeaders({ ...config.headers, 'X-CSRFToken': csrftoken, withCredentials: true });
  }

  if (config.params) {
    newConfig.params = decamelizeKeys(config.params);
  }

  if (config.data) {
    // Multipart form data needs to be transformed into kebab-case.
    if (config.data instanceof FormData) {
      const formData = new FormData();
      // Complains about `config.data.entries` - decamelize each key in `config.data`
      for (const [k, v] of config.data.entries()) {
        formData.append(decamelize(k, { separator: '-' }), v);
      }
      newConfig.data = formData;
    } else {
      newConfig.data = decamelizeKeys(config.data);
    }
  }

  return newConfig as InternalAxiosRequestConfig;
};

// Setup middleware
axiosInstance.interceptors.request.use(onRequest);
axiosInstance.interceptors.response.use(onResponse);

export default axiosInstance;
